/**
 * Modal to create + submit a Budget Exceedance against a spend document.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Space, Typography, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ENTITY_TYPE_PROJECT_BUDGET_EXCEEDANCE } from '@/constants/approval-entity-types';
import { useProject } from '@/hooks/useProjects';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;
const { TextArea } = Input;

export type BudgetExceedanceRequestModalProps = {
  open: boolean;
  onClose: () => void;
  projectId: string;
  sourceEntityType: string;
  sourceEntityId: string;
  lines: Array<Record<string, unknown>>;
  checkResult?: {
    breaches?: Array<{ message?: string; over?: number }>;
    totals?: { over?: number; requested?: number; available?: number };
  } | null;
  onSubmitted?: (exceedanceId: string) => void;
};

export const BudgetExceedanceRequestModal: React.FC<BudgetExceedanceRequestModalProps> = ({
  open,
  onClose,
  projectId,
  sourceEntityType,
  sourceEntityId,
  lines,
  checkResult,
  onSubmitted,
}) => {
  const qc = useQueryClient();
  const { data: project } = useProject(projectId);
  const [reason, setReason] = useState('');
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [exceedanceId, setExceedanceId] = useState<string | null>(null);

  useEffect(() => {
    if (project?.department_id) {
      setDepartmentId(project.department_id);
    }
  }, [project?.department_id]);

  const createM = useMutation({
    mutationFn: async () => {
      if (!departmentId) {
        throw new Error('Department is required');
      }
      const created = await projectService.createBudgetExceedance(projectId, {
        sourceEntityType,
        sourceEntityId,
        reason: reason.trim() || undefined,
        department_id: departmentId,
        lines,
      });
      const id = String(created.id);
      await projectService.submitBudgetExceedance(projectId, id);
      return id;
    },
    onSuccess: (id) => {
      message.success('Budget exceedance submitted for approval');
      setExceedanceId(id);
      qc.invalidateQueries({ queryKey: ['project-budget-exceedances', projectId] });
      onSubmitted?.(id);
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Exceedance failed'),
  });

  const over = Number(checkResult?.totals?.over || 0);

  return (
    <Modal
      open={open}
      title="Request Budget Exceedance"
      onCancel={onClose}
      okText={exceedanceId ? 'Close' : 'Submit for approval'}
      onOk={() => {
        if (exceedanceId) {
          onClose();
          return;
        }
        if (!departmentId) {
          message.error('Department is required');
          return;
        }
        createM.mutate();
      }}
      confirmLoading={createM.isPending}
      width={640}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="warning"
          showIcon
          message="Spend exceeds Current Budget availability"
          description={
            <>
              Over by <strong>{formatMoney(over)}</strong>. An approved Budget Exceedance authorizes
              overspend — it does <strong>not</strong> change the Current Budget. Use a Budget
              Revision if you need to increase the baseline.
            </>
          }
        />
        {(checkResult?.breaches || []).slice(0, 5).map((b, i) => (
          <Text key={i} type="secondary" style={{ display: 'block' }}>
            • {b.message}
          </Text>
        ))}
        {!exceedanceId && (
          <>
            <div>
              <Text type="secondary">Department</Text>
              <DepartmentSelect
                style={{ width: '100%', marginTop: 8 }}
                value={departmentId}
                onChange={(v) => setDepartmentId(v)}
                placeholder="Select department"
              />
            </div>
            <div>
              <Text type="secondary">Reason</Text>
              <TextArea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this overspend required?"
                style={{ marginTop: 8 }}
              />
            </div>
          </>
        )}
        {exceedanceId && (
          <ApprovalPanel
            entityType={ENTITY_TYPE_PROJECT_BUDGET_EXCEEDANCE}
            entityId={exceedanceId}
            extraInvalidateQueryKeys={[['project-budget-exceedances', projectId]]}
          />
        )}
      </Space>
    </Modal>
  );
};

export default BudgetExceedanceRequestModal;
